import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, card, btn, inp } from './adminHelpers';

function ContractRow({ c, verify }) {
  const [hovered, setHovered] = useState(false);
  const isSigned = !!c.signed_at;

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
        transition: 'background 0.2s',
      }}
    >
      {/* Contract ID */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 }}>
          CON-{String(c.id).padStart(5, '0')}
        </span>
      </td>

      {/* Customer Info */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{c.Booking?.customer?.name || 'Unknown'}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>{c.Booking?.customer?.email}</div>
      </td>

      {/* Booking details */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, color: '#fff' }}>Booking #{c.booking_id}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>
          {c.Booking ? `${c.Booking.booth_type} (${(() => { const [y,m,d] = c.Booking.event_date.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString(); })()})` : '—'}
        </div>
      </td>

      {/* Signed Status */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{
          display: 'inline-block',
          background: isSigned ? 'rgba(39,200,90,0.1)' : 'rgba(245,166,35,0.1)',
          border: `1px solid ${isSigned ? 'rgba(39,200,90,0.3)' : 'rgba(245,166,35,0.3)'}`,
          color: isSigned ? '#27C85A' : '#f5a623',
          fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {isSigned ? 'Signed' : 'Unsigned'}
        </span>
      </td>

      {/* Sign Date */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 12, color: '#e0e0e0' }}>
        {isSigned ? new Date(c.signed_at).toLocaleString() : <span style={{ color: '#555', fontStyle: 'italic' }}>Awaiting Sign</span>}
      </td>

      {/* Verification */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{
          display: 'inline-block',
          background: c.verified ? 'rgba(39,200,90,0.15)' : 'rgba(255,255,255,0.05)',
          border: `1px solid ${c.verified ? 'rgba(39,200,90,0.4)' : 'rgba(255,255,255,0.1)'}`,
          color: c.verified ? '#27C85A' : '#777',
          fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {c.verified ? 'Verified' : 'Unverified'}
        </span>
      </td>

      {/* Actions */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {c.pdf_url && (
            <a
              href={c.pdf_url}
              target="_blank"
              rel="noreferrer"
              style={{ ...btn('rgba(255,255,255,0.05)', true), border: '1px solid rgba(255,255,255,0.1)', textDecoration: 'none', fontSize: 11, padding: '6px 12px', borderRadius: 8 }}
            >
              ⬇ PDF
            </a>
          )}
          {isSigned && (
            <button
              onClick={() => verify(c.id)}
              style={{ ...btn(c.verified ? '#555' : '#27C85A', true), padding: '6px 12px', fontSize: 11, borderRadius: 8 }}
            >
              {c.verified ? 'Unverify' : 'Verify'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function ContractsTab() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const r = await ax().get(`${API}/api/admin/contracts`, { params });
      setContracts(r.data.contracts);
      setTotal(r.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const verify = async (id) => {
    try {
      await ax().patch(`${API}/api/admin/contracts/${id}/verify`);
      load();
    } catch (err) {
      alert("Failed to update verification status");
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>📄 Signed & Unsigned Contracts</h2>

      {/* Filter & Search Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Search Customer</label>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by customer name or email…"
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Start Date</label>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>End Date</label>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        {(search || startDate || endDate) && (
          <button onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); setPage(1); }}
            style={{ ...btn('#555', true), padding: '10px 16px', borderRadius: 10, alignSelf: 'flex-end', height: 40 }}>
            Reset
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#a0a0b8', padding: 20 }}>Loading contracts…</p>
      ) : contracts.length === 0 ? (
        <p style={{ color: '#a0a0b8', padding: 20 }}>No contracts found matching current filters.</p>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                  {['Contract ID', 'Customer Info', 'Booking Details', 'Signed Status', 'Sign Date', 'Verification', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => (
                  <ContractRow key={c.id} c={c} verify={verify} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {total > 15 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          {page > 1 && <button onClick={() => setPage(p => p - 1)} style={btn('#9F5CFF', true)}>← Prev</button>}
          <span style={{ color: '#a0a0b8', fontSize: 13, alignSelf: 'center' }}>Page {page} of {Math.ceil(total / 15)}</span>
          {page * 15 < total && <button onClick={() => setPage(p => p + 1)} style={btn('#9F5CFF', true)}>Next →</button>}
        </div>
      )}
    </div>
  );
}
